// Serializable employee jobs. Rendering never drives production or delivery.
import { CELLAR, MILK, RICOTTA, STAFF, VAT, type SupplyId, type TaskId } from '../config/constants';
import { CHEESES } from '../data/cheeses';
import * as B from '../process/Batch';
import * as Cellar from './Cellar';
import * as Staff from './Staff';
import type { Employee, InventoryCheese, SaveState } from './GameState';
import { absMinute } from './GameState';
import { advanceStaffMovement, clearStaffNavigation, ensureStaffPosition, reserveStaffTarget, staffAtTarget } from './StaffNavigation';

type Target = { room: 'barn' | 'creamery' | 'cellar' | 'field'; objectId: string };
type Step = { target: Target; label: string; minutes: number; tasks: TaskId[] };
const cow: Target = { room: 'barn', objectId: 'bessie' };
const vat: Target = { room: 'creamery', objectId: 'vat' };
const table: Target = { room: 'creamery', objectId: 'table' };
const tub: Target = { room: 'creamery', objectId: 'tub' };
const racks: Target = { room: 'cellar', objectId: 'rack_west' };
const market: Target = { room: 'creamery', objectId: 'board' };
const steps: Record<B.Process, Step[]> = {
  mozzarella: [
    { target: vat, label: 'Pasteurizing and setting curds', minutes: 50, tasks: ['pasteurize', 'curdle', 'rennet'] },
    { target: table, label: 'Cutting and draining', minutes: 10, tasks: ['cut'] },
    { target: tub, label: 'Stretching and packaging', minutes: 10, tasks: ['stretch', 'bag'] },
  ],
  ricotta: [
    { target: vat, label: 'Heating and acidifying whey', minutes: 35, tasks: ['pasteurize', 'curdle'] },
    { target: table, label: 'Skimming, draining and packaging', minutes: 20, tasks: ['cut', 'bag'] },
  ],
  taleggio: [
    { target: vat, label: 'Pasteurizing, culturing and setting', minutes: 85, tasks: ['pasteurize', 'culture', 'rennet'] },
    { target: table, label: 'Molding and turning curds', minutes: 60, tasks: ['cut'] },
    { target: tub, label: 'Brining', minutes: 30, tasks: ['brine'] },
  ],
};

export interface StaffContext {
  rng: () => number;
  recipeBlock: (id: string) => string | null;
  toast: (message: string) => void;
  now?: number;
}
const has = (s: SaveState, id: string): boolean => s.skills.includes(id);
function wait(e: Employee, reason: string): void {
  e.jobStatus = 'waiting';
  e.jobLabel = 'Waiting';
  e.blockedReason = reason;
}
function workTarget(s: SaveState, e: Employee): Target {
  if (e.job?.kind === 'cheese' && e.job.batch) return steps[e.job.batch.process][e.job.step].target;
  return e.station === 'barn' ? (s.bessie.location === 'pasture' && has(s, 'field') ? { room: 'field', objectId: 'pasture' } : cow) : e.station === 'cellar' ? racks : vat;
}
function deliveryTarget(e: Employee): Target {
  if (e.job?.kind === 'milk') return vat;
  if (e.job?.kind === 'cheese' && e.job.batch?.process === 'taleggio') return racks;
  return market;
}

function startJob(s: SaveState, e: Employee, ctx: StaffContext): boolean {
  if (e.station === 'barn') {
    if (s.bessie.milkingsToday >= MILK.MILKINGS_PER_DAY || (s.bessie.lastMilkedMinute !== null && (ctx.now ?? absMinute(s)) - s.bessie.lastMilkedMinute < MILK.COOLDOWN_MINUTES)) {
      wait(e, 'Bessie needs time before the next milking.'); return false;
    }
    if (!s.bessie.fedToday) {
      const feed = s.supplies.feed_basic > 0 ? 'feed_basic' : 'feed_quality';
      if (s.supplies[feed] <= 0) { wait(e, 'No feed available.'); return false; }
      s.supplies[feed]--;
      s.bessie.fedToday = true;
      s.bessie.feedType = feed === 'feed_basic' ? 'basic' : 'quality';
      Staff.grantXp(e, 'feed', 2);
    }
    // Reserve this milking immediately so the player cannot consume it too.
    s.bessie.milkingsToday++;
    s.bessie.lastMilkedMinute = ctx.now ?? absMinute(s);
    const feed = MILK.FEED[s.bessie.feedType ?? 'basic'];
    const score = Math.min(1, Staff.skillFactor(e, 'milk') + (has(s, 'anti_gravity_milker') ? 0.1 : 0) + (has(s, 'staff_accuracy_1') ? 0.05 : 0));
    const gallons = Math.round((MILK.YIELD_MIN + ctx.rng() * (MILK.YIELD_MAX - MILK.YIELD_MIN)) * (MILK.SCORE_YIELD_BASE + MILK.SCORE_YIELD_RANGE * score) * (s.bessie.location === 'pasture' && has(s, 'field') ? 1.15 : 1) * (1 + 0.01 * (s.coinUpgradeLevels?.bessie_care ?? 0)) * 10) / 10;
    e.job = { coinUpgradeSnapshot: { ...s.coinUpgradeLevels }, kind: 'milk', phase: 'work', step: 0, elapsed: 0, duration: 15, products: [], input: {
      kind: 'milk', qty: gallons, quality: Math.min(5, Math.max(1, feed.qualityBase + MILK.SCORE_QUALITY_BONUS * score + (e.stats.hygiene - 5) * 0.02)),
      cream: Math.round((feed.cream + (ctx.rng() * 2 - 1) * feed.creamVar) * 10) / 10,
      madeMinute: ctx.now ?? absMinute(s), ownerId: e.id, ownerName: e.name,
    } };
    return true;
  }
  if (e.station === 'vat') {
    const id = s.staff.vatRecipe;
    const def = id ? CHEESES[id] : null;
    if (!def?.process || ctx.recipeBlock(def.id)) { wait(e, 'Select an unlocked recipe.'); return false; }
    const ricotta = def.process === 'ricotta';
    const input = s.inventory.find(i => i.kind === (ricotta ? 'whey' : 'milk') && i.qty >= (ricotta ? RICOTTA.MIN_GALLONS : 0.1));
    const needs: SupplyId[] = ricotta ? ['citric_acid'] : def.process === 'mozzarella' ? ['citric_acid', 'rennet'] : ['culture', 'rennet', 'salt'];
    if (!input || input.kind === 'cheese') { wait(e, ricotta ? 'Waiting for whey.' : 'Waiting for delivered milk.'); return false; }
    if (needs.some(n => s.supplies[n] < 1)) { wait(e, 'Missing recipe supplies.'); return false; }
    const gallons = Math.min(MILK.VAT_CAPACITY_GAL, input.qty);
    const batch = B.createBatch(def.process, gallons, input.quality, input.kind === 'milk' ? input.cream : 0, ctx.now ?? absMinute(s), e.id, e.name, has(s, 'stardust_precision_matrix'));
    batch.coinUpgradeSnapshot = { ...s.coinUpgradeLevels };
    input.qty = Math.round((input.qty - gallons) * 10) / 10;
    if (input.qty <= 0) s.inventory.splice(s.inventory.indexOf(input), 1);
    for (const need of needs) s.supplies[need]--;
    if (s.vat.durability <= VAT.WORN_THRESHOLD) B.penalize(batch, absMinute(s), VAT.WORN_PENALTY, 'Worn vat');
    e.job = { kind: 'cheese', phase: 'work', step: 0, elapsed: 0, duration: steps[def.process][0].minutes, recipeId: def.id, batch, products: [] };
    return true;
  }
  if (e.station === 'cellar') {
    // Retrieve employee-made cheese at peak age; the player's racks remain their choice.
    const ready = s.cellar.racks.find(r => r.ownerId && r.ownerId !== 'player' && r.ageDays >= (Cellar.ageRule(r.cheeseId)?.peakDays ?? Infinity));
    if (ready) {
      if (s.supplies.packaging < ready.qty) { wait(e, 'Packaging needed for aged cheese.'); return false; }
      const item = Cellar.takeOut(s, ready.id, ready.qty);
      if (!item) return false;
      s.inventory.splice(s.inventory.indexOf(item), 1); // carried, not yet delivered to market
      s.supplies.packaging -= item.qty;
      e.job = { kind: 'retrieval', phase: 'deliver', step: 0, elapsed: 0, duration: 0, products: [item] };
      clearStaffNavigation(e);
      return true;
    }
    if (Cellar.stacksNeedingWash(s).length === 0 && Cellar.tempOk(s.cellar.temp) && Cellar.humOk(s.cellar.humidity)) {
      wait(e, 'Racks are tended; waiting for aged cheese.'); return false;
    }
    if (Cellar.stacksNeedingWash(s).length && s.supplies.salt < CELLAR.SALT_PER_WASH) { wait(e, 'Salt needed for rind washing.'); return false; }
    e.job = { coinUpgradeSnapshot: { ...s.coinUpgradeLevels }, kind: 'cellar', phase: 'work', step: 0, elapsed: 0, duration: 10, products: [] };
    return true;
  }
  return false;
}

function completeJob(s: SaveState, e: Employee): void {
  e.job = undefined;
  e.jobProgress = 0;
  e.jobStatus = 'idle';
  e.jobLabel = 'Idle';
  e.blockedReason = null;
  clearStaffNavigation(e);
  if (e.pendingStation !== undefined) { e.station = e.pendingStation; e.pendingStation = undefined; }
  if (e.leaving) s.staff.employees.splice(s.staff.employees.indexOf(e), 1);
}

function completeWork(s: SaveState, e: Employee, ctx: StaffContext): void {
  const j = e.job!;
  if (j.kind === 'milk') {
    j.products = [{ ...j.input!, madeMinute: ctx.now ?? absMinute(s) }];
    j.input = undefined;
    Staff.grantXp(e, 'milk', 16 * (1 + 0.02 * (j.coinUpgradeSnapshot?.crew_practice ?? s.coinUpgradeLevels?.crew_practice ?? 0)));
  } else if (j.kind === 'cheese' && j.batch) {
    const b = j.batch;
    const chain = steps[b.process];
    const step = chain[j.step];
    const final = j.step === chain.length - 1;
    // Reserve packaging before rolling quality, so retries cannot reroll the batch.
    if (final && b.process !== 'taleggio' && s.supplies.packaging < B.projectedUnits(b)) {
      wait(e, 'Buy packaging to finish this batch.'); return;
    }
    const accuracy = has(s, 'staff_accuracy_1') ? 0.05 : 0;
    const result = Staff.employeeBatch(e, b.quality, step.tasks, ctx.rng, accuracy, final);
    b.quality = result.quality;
    b.yieldFactor *= result.yieldFactor;
    for (const task of step.tasks) Staff.grantXp(e, task, STAFF.XP_PER_STAGE * (1 + 0.02 * (s.coinUpgradeLevels?.crew_practice ?? 0)));
    if (!final) {
      j.step++;
      j.elapsed = 0;
      j.duration = chain[j.step].minutes;
      e.jobProgress = 0;
      e.jobStatus = 'carrying';
      clearStaffNavigation(e);
      return;
    }
    const n = B.projectedUnits(b);
    const product: InventoryCheese = { kind: 'cheese', cheeseId: j.recipeId!, qty: n, stars: B.stars(b), madeDay: s.day, ownerId: e.id, ownerName: e.name };
    j.products = n > 0 ? [product] : [];
    if (b.process !== 'taleggio') s.supplies.packaging -= n;
    if (b.process !== 'ricotta') s.inventory.push({ kind: 'whey', qty: Math.round(b.gallons * 0.8 * 10) / 10, quality: b.quality });
    s.vat.durability = Math.max(0, s.vat.durability - VAT.WEAR_PER_BATCH * (1 - 0.02 * (b.coinUpgradeSnapshot?.sturdy_vat ?? s.coinUpgradeLevels?.sturdy_vat ?? 0)));
    s.stats.batches++;
  } else if (j.kind === 'cellar') {
    s.cellar.temp = (CELLAR.TEMP_LOW + CELLAR.TEMP_HIGH) / 2;
    s.cellar.humidity = (CELLAR.HUM_LOW + CELLAR.HUM_HIGH) / 2;
    if (Cellar.stacksNeedingWash(s).length && s.supplies.salt >= CELLAR.SALT_PER_WASH) {
      s.supplies.salt -= CELLAR.SALT_PER_WASH;
      Cellar.washDue(s);
    }
    Staff.grantXp(e, 'cellar', 8 * (1 + .02 * (j.coinUpgradeSnapshot?.crew_practice ?? s.coinUpgradeLevels.crew_practice ?? 0)));
    completeJob(s, e); return;
  }
  j.phase = 'deliver';
  e.jobStatus = 'carrying';
  e.jobProgress = 0;
  clearStaffNavigation(e);
}

function deliver(s: SaveState, e: Employee, ctx: StaffContext): void {
  const j = e.job!;
  if (j.kind === 'cheese' && j.batch?.process === 'taleggio') {
    const cheese = j.products.find((p): p is InventoryCheese => p.kind === 'cheese');
    if (cheese) {
      const n = Math.min(cheese.qty, Cellar.rackSpace(s));
      if (n > 0) {
        Cellar.rackWheels(s, cheese.cheeseId, n, cheese.stars, cheese.ownerId, cheese.ownerName);
        cheese.qty -= n;
      }
      if (cheese.qty > 0) { wait(e, 'Cellar racks are full. Holding the remaining cheese.'); return; }
    }
  } else {
    for (const item of j.products) if (item.qty > 0) s.inventory.push(item);
  }
  ctx.toast(`${e.name} delivered ${j.kind === 'milk' ? 'milk to the vat' : 'the finished cheese'}.`);
  completeJob(s, e);
}

function advanceOne(s: SaveState, e: Employee, minutes: number, ctx: StaffContext): void {
  if (!e.presentToday || !e.station || !ensureStaffPosition(s, e)) return;
  if (e.leaving && !e.job) { completeJob(s, e); return; }
  const delivering = e.job?.phase === 'deliver';
  const target = delivering ? deliveryTarget(e) : workTarget(s, e);
  if (!reserveStaffTarget(s, e, target, ctx.rng, delivering ? 'delivery' : 'work')) {
    wait(e, e.navigationBlocked ?? 'Waiting for a free station position.'); return;
  }
  if (!staffAtTarget(e)) {
    e.jobStatus = delivering || (e.job?.step ?? 0) > 0 ? 'carrying' : 'walking';
    e.jobLabel = `${e.jobStatus === 'carrying' ? 'Carrying to' : 'Walking to'} ${target.objectId}`;
    e.blockedReason = null;
    advanceStaffMovement(s, e, minutes);
    if (e.navigationBlocked) wait(e, e.navigationBlocked);
    return; // time spent moving must not also count as work
  }
  if (delivering) { deliver(s, e, ctx); return; }
  if (!e.job && !startJob(s, e, ctx)) return;
  if (!e.job || e.job.phase === 'deliver') return;
  e.jobStatus = 'working';
  e.blockedReason = null;
  e.jobLabel = e.job.kind === 'milk' ? 'Milking Bessie' : e.job.kind === 'cheese' && e.job.batch ? steps[e.job.batch.process][e.job.step].label : 'Tending racks';
  const speed = (0.8 + e.stats.workEthic * 0.04) * (has(s, 'staff_speed_1') ? 1.1 : 1);
  e.job.elapsed = Math.min(e.job.duration, e.job.elapsed + minutes * speed);
  e.jobProgress = e.job.elapsed / e.job.duration;
  if (e.job.elapsed >= e.job.duration) completeWork(s, e, ctx);
}

export function advanceStaffJobs(s: SaveState, minutes: number, ctx: StaffContext): void {
  // Substeps make large time advances obey the same movement/work rules as frames.
  for (let remaining = minutes; remaining > 1e-8; remaining -= Math.min(0.1, remaining)) {
    const dt = Math.min(0.1, remaining);
    const frameContext = { ...ctx, now: absMinute(s) - remaining + dt };
    for (const e of [...s.staff.employees]) advanceOne(s, e, dt, frameContext);
  }
}
